import Button from '@root/core/src/components/button';
import Card from '@root/core/src/components/card';
import React, { useEffect } from '@root/vendor/react';
import Responsive from '@root/core/src/utils/responsive';
import Shadows from '@root/brand/src/utils/shadows';
import bridgeErrorIcon from '@root/agents.joinroot.com/src/assets/bridge-error-icon.svg';
import environment from '@root/core/src/utils/environment';
import useAnalytics from '@root/core/src/hooks/use-analytics';
import { BUTTON_VARIANT_SECONDARY } from '@root/core/src/components/button/styles';
import { Colors, StyleSheet, Theme } from '@root/core/src/utils/styles';
import { useHistory, useLocation } from '@root/core/src/components/route';
import { useLogger } from '@root/agents.joinroot.com/src/utils/logging';

export default function BridgeError() {
  const history = useHistory();
  const location = useLocation();
  const logger = useLogger();
  const searchParams = new URLSearchParams(location.search);
  const reason = searchParams.get('reason') === 'not_found' ? 'not_found' : 'error';
  const retryUrl = searchParams.get('retry');
  const { trackClick, trackEvent } = useAnalytics('BRIDGE_ERROR', false);

  useEffect(() => {
    trackEvent('VIEWED', { reason });
  }, [reason, trackEvent]);

  const isNotFound = reason === 'not_found';
  const handleGoToRootAgentPlatform = () => {
    trackClick('GO_TO_ROOT_AGENT_PLATFORM');
    history.push('/welcome/policies');
  };

  const handleClick = () => {
    if (isNotFound) {
      handleGoToRootAgentPlatform();
      return;
    }

    trackClick('REFRESH_PAGE');

    if (retryUrl) {
      try {
        // Locally, API_BASE_URL can point to the webpack dev server on port 8282; webpack.config.js
        // proxies /agent_api requests to Rails on port 3000. Retry URLs can point directly to Rails,
        // using localhost instead of 127.0.0.1, so their origins may differ. Deployed API_BASE_URL points to Rails.
        if (environment.environment === 'local') {
          // eslint-disable-next-line root/prevent-use-of-window-location
          window.location.assign(retryUrl);
          return;
        }

        const retry = new URL(retryUrl);
        const apiUrl = new URL(environment.apiBaseUrl);
        // eslint-disable-next-line root/prevent-use-of-window-location
        const isTrustedOrigin = retry.origin === apiUrl.origin || retry.origin === window.location.origin;
        if (isTrustedOrigin && retry.pathname === '/agent_api/bridge') {
          // eslint-disable-next-line root/prevent-use-of-window-location
          window.location.assign(retry.toString());
          return;
        }
        logger.error('BridgeError: retry URL did not match the bridge endpoint', {
          reason,
          pathname: retry.pathname,
        });
      } catch (error) {
        logger.error('BridgeError: could not parse retry URL', {
          reason,
          errorName: error.name,
        });
      }
    } else {
      logger.error('BridgeError: refresh clicked without a retry URL', { reason });
    }

    history.push('/welcome/policies');
  };

  return (
    <div
      css={styles.page}
      data-testid={'bridge-error'}
    >
      <Card cssOverrides={styles.card}>
        <div css={styles.iconWrapper}>
          <img
            alt={''}
            css={styles.icon}
            src={bridgeErrorIcon}
          />
        </div>
        <h1 css={styles.title}>{isNotFound ? 'Quote not found' : 'We couldn\'t load this page'}</h1>
        {isNotFound ? (
          <>
            <p css={styles.body}>
              The quote you&apos;re looking for has either expired or has already been bound to an active policy.
            </p>
            <h2 css={styles.sectionTitle}>What you can do</h2>
            <p css={styles.body}>
              Visit the Root Agent Platform to view your active policies, in-progress quotes, or to start a new quote.
            </p>
          </>
        ) : (
          <>
            <p css={styles.body}>
              Try refreshing the page, or visit the Root Agent Platform to view your in-progress quotes or to start a new quote.
            </p>
            <p css={styles.body}>
              If the issue persists, contact Agent Support at{' '}
              <a href={'mailto:agentsupport@joinroot.com'}>agentsupport@joinroot.com</a> or{' '}
              <a href={'tel:8336591458'}>(833)-659-1458</a>.
            </p>
          </>
        )}
        <Button
          onClick={handleClick}
          type={'button'}
        >
          {isNotFound ? 'Go to Root Agent Platform' : 'Refresh the page'}
        </Button>
        {!isNotFound && (
          <Button
            onClick={handleGoToRootAgentPlatform}
            type={'button'}
            variant={BUTTON_VARIANT_SECONDARY}
          >
            Go to Root Agent Platform
          </Button>
        )}
      </Card>
    </div>
  );
}

const styles = StyleSheet.create({
  body: {
    ...Theme.paragraph1(),
    margin: '0 0 16px',
  },
  card: {
    background: Colors.white(),
    boxSizing: 'border-box',
    margin: 'auto',
    maxWidth: 480,
    padding: 40,
    width: '100%',
    ...Shadows.cardShadow(),
    ...Responsive.md({
      padding: 40,
    }),
    a: {
      color: Colors.gray50(),
      textDecoration: 'underline',
    },
  },
  icon: {
    height: 112,
    width: 112,
  },
  iconWrapper: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 24,
  },
  page: {
    background: Colors.gray10(),
    boxSizing: 'border-box',
    display: 'flex',
    minHeight: '100vh',
    padding: 16,
  },
  sectionTitle: {
    ...Theme.paragraph1({ bold: true }),
    color: Colors.nearBlack(),
    margin: '0 0 8px',
  },
  title: {
    ...Theme.heading2(),
    margin: '0 0 12px',
  },
});
